import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Box,
  CheckCircle2,
  ChevronLeft,
  Clock3,
  Loader2,
  Maximize2,
  Package,
  QrCode,
  Store,
  Tag,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

type PanelProduct = {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  brand?: string | null;
  salePrice: string | number;
  imageUrl?: string | null;
  unit?: string | null;
};

type ProductSalesPanelProps = {
  salonSlug: string;
  salonName: string;
  salonColor?: string;
  logoUrl?: string | null;
};

const PRODUCTS_PER_SCREEN = 4;

function formatPrice(value: string | number) {
  return `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
}

export function ProductSalesPanel({
  salonSlug,
  salonName,
  salonColor = "#C9A96E",
  logoUrl,
}: ProductSalesPanelProps) {
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [page, setPage] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<PanelProduct | null>(
    null
  );

  const { data: products = [], isLoading } = trpc.products.listPublic.useQuery(
    { salonSlug },
    { enabled: Boolean(salonSlug), refetchInterval: 30_000 }
  );

  const saleProducts = products as PanelProduct[];
  const pageCount = Math.max(
    1,
    Math.ceil(saleProducts.length / PRODUCTS_PER_SCREEN)
  );
  const visibleProducts = useMemo(
    () =>
      saleProducts.slice(
        page * PRODUCTS_PER_SCREEN,
        (page + 1) * PRODUCTS_PER_SCREEN
      ),
    [page, saleProducts]
  );
  const storeUrl = salonSlug
    ? `${window.location.origin}/s/${salonSlug}/loja`
    : window.location.origin;

  useEffect(() => {
    const timer = window.setInterval(() => setCurrentTime(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setPage(currentPage => Math.min(currentPage, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  useEffect(() => {
    if (pageCount <= 1) return;
    const timer = window.setInterval(() => {
      setPage(currentPage => (currentPage + 1) % pageCount);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [pageCount]);

  function handleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    document.documentElement.requestFullscreen();
  }

  const productMeta = selectedProduct
    ? ([
        selectedProduct.category && {
          label: "Categoria",
          value: selectedProduct.category,
          icon: Tag,
        },
        selectedProduct.brand && {
          label: "Marca",
          value: selectedProduct.brand,
          icon: Store,
        },
        selectedProduct.unit && {
          label: "Unidade",
          value: selectedProduct.unit,
          icon: Box,
        },
      ].filter(Boolean) as Array<{
        label: string;
        value: string;
        icon: typeof Tag;
      }>)
    : [];

  return (
    <div
      className="min-h-screen overflow-hidden text-white"
      style={{
        background:
          "linear-gradient(135deg, #0a0a0f 0%, #111118 60%, #0d0d15 100%)",
      }}
    >
      <header
        className="flex items-center justify-between gap-4 border-b border-white/10 px-4 py-3 sm:px-6"
        style={{
          background: "rgba(255,255,255,0.03)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={salonName}
              className="h-10 w-10 shrink-0 rounded-xl border border-white/10 object-cover"
              style={{ boxShadow: `0 0 16px ${salonColor}40` }}
            />
          ) : (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10"
              style={{ background: `${salonColor}20` }}
            >
              <Store className="h-5 w-5" style={{ color: salonColor }} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold tracking-wide sm:text-lg">
              {salonName}
            </h1>
            <p className="flex items-center gap-1 text-xs text-white/45">
              <Store className="h-3 w-3" />
              Painel de Vendas
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <p className="text-xl font-bold tabular-nums tracking-tight sm:text-2xl">
              {format(currentTime, "HH:mm", { locale: ptBR })}
            </p>
            <p className="hidden text-xs capitalize text-white/40 sm:block">
              {format(currentTime, "EEE, dd/MM", { locale: ptBR })}
            </p>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={handleFullscreen}
            className="text-white/45 hover:bg-white/10 hover:text-white"
            aria-label="Alternar tela cheia"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="mx-auto flex min-h-[calc(100dvh-73px)] max-w-7xl flex-col px-4 py-4 sm:px-6 sm:py-6">
        <div className="mb-4 flex items-end justify-between gap-4 sm:mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">
              Vitrine do catálogo
            </p>
            <h2 className="mt-1 text-xl font-bold sm:text-3xl">
              Produtos para levar hoje
            </h2>
            <p className="mt-1 text-xs text-white/45 sm:text-sm">
              Toque em um produto para ver informações completas.
            </p>
          </div>
          {saleProducts.length > PRODUCTS_PER_SCREEN && (
            <div className="hidden items-center gap-1.5 sm:flex">
              {Array.from({ length: pageCount }).map((_, index) => (
                <button
                  type="button"
                  key={index}
                  onClick={() => setPage(index)}
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: index === page ? 22 : 7,
                    backgroundColor:
                      index === page ? salonColor : "rgba(255,255,255,0.25)",
                  }}
                  aria-label={`Exibir página ${index + 1} do catálogo`}
                />
              ))}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2
              className="h-9 w-9 animate-spin"
              style={{ color: salonColor }}
            />
          </div>
        ) : visibleProducts.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-3xl border border-dashed border-white/10 bg-white/[0.02] px-6 text-center">
            <Package className="h-14 w-14 text-white/20" />
            <p className="mt-4 font-semibold text-white/70">
              Nenhum produto disponível na vitrine
            </p>
            <p className="mt-1 max-w-sm text-sm text-white/40">
              Cadastre produtos ativos com estoque e imagem para exibi-los neste
              painel.
            </p>
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-2 content-start gap-3 sm:grid-cols-4 sm:gap-5">
            {visibleProducts.map(product => (
              <button
                type="button"
                key={product.id}
                onClick={() => setSelectedProduct(product)}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] text-left shadow-2xl transition duration-200 hover:-translate-y-1 hover:border-white/25 focus-visible:outline-none focus-visible:ring-2"
                style={{ outlineColor: salonColor }}
                aria-label={`Ver detalhes de ${product.name}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-white/[0.04]">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Package className="h-14 w-14 text-white/20" />
                    </div>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent px-3 pb-3 pt-10 text-xs font-medium text-white">
                    Ver detalhes
                  </span>
                  {product.category && (
                    <Badge className="absolute left-2 top-2 border-0 bg-black/65 text-[10px] text-white">
                      {product.category}
                    </Badge>
                  )}
                </div>
                <div className="space-y-1.5 p-3 sm:p-4">
                  {product.brand && (
                    <p
                      className="truncate text-[11px] font-medium"
                      style={{ color: `${salonColor}dd` }}
                    >
                      {product.brand}
                    </p>
                  )}
                  <p className="line-clamp-2 min-h-9 text-sm font-semibold leading-tight text-white sm:text-base">
                    {product.name}
                  </p>
                  <p
                    className="text-lg font-black sm:text-xl"
                    style={{ color: salonColor }}
                  >
                    {formatPrice(product.salePrice)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <section className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 sm:mt-5 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden rounded-xl bg-white p-1.5 sm:block">
              <QRCodeSVG
                value={storeUrl}
                size={56}
                bgColor="#ffffff"
                fgColor="#0a0a0f"
                level="M"
              />
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold">
                <QrCode className="h-4 w-4" style={{ color: salonColor }} />
                Compre pelo celular
              </p>
              <p className="mt-0.5 text-xs text-white/45">
                Aponte a câmera para acessar a loja e enviar seu pedido.
              </p>
            </div>
          </div>
          <span className="hidden text-xs text-white/30 lg:block">
            Atualiza o catálogo automaticamente
          </span>
        </section>
      </main>

      <Dialog
        open={Boolean(selectedProduct)}
        onOpenChange={open => !open && setSelectedProduct(null)}
      >
        {selectedProduct && (
          <DialogContent
            showCloseButton={false}
            className="gap-0 overflow-hidden border-0 bg-white p-0 text-slate-950 max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:h-[100dvh] max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none sm:max-w-4xl sm:rounded-2xl"
          >
            <div className="flex h-full min-h-0 flex-col sm:grid sm:grid-cols-[minmax(0,1.1fr)_minmax(20rem,0.9fr)]">
              <div className="relative shrink-0 overflow-hidden bg-slate-100 max-sm:aspect-[4/5] sm:min-h-[540px]">
                {selectedProduct.imageUrl ? (
                  <img
                    src={selectedProduct.imageUrl}
                    alt={selectedProduct.name}
                    className="h-full w-full object-cover sm:object-contain"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Package className="h-20 w-20 text-slate-300" />
                  </div>
                )}
                <DialogClose asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute left-3 top-3 h-10 w-10 rounded-full bg-white/95 shadow-md hover:bg-white sm:left-auto sm:right-3"
                    aria-label="Voltar para a vitrine"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                </DialogClose>
                {selectedProduct.category && (
                  <Badge className="absolute bottom-3 left-3 border-0 bg-black/70 px-2.5 py-1 text-xs text-white">
                    {selectedProduct.category}
                  </Badge>
                )}
              </div>

              <div className="flex min-h-0 flex-1 flex-col bg-white">
                <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-5 sm:px-7 sm:pt-8">
                  <DialogHeader className="items-start text-left">
                    {selectedProduct.brand && (
                      <DialogDescription
                        className="font-medium"
                        style={{ color: salonColor }}
                      >
                        {selectedProduct.brand}
                      </DialogDescription>
                    )}
                    <DialogTitle className="pr-8 text-2xl leading-tight text-slate-950 sm:text-3xl">
                      {selectedProduct.name}
                    </DialogTitle>
                  </DialogHeader>

                  <p
                    className="mt-5 text-3xl font-bold tracking-tight"
                    style={{ color: salonColor }}
                  >
                    {formatPrice(selectedProduct.salePrice)}
                  </p>

                  <div className="mt-5 border-y border-slate-200 py-4">
                    <p className="text-sm font-semibold text-slate-900">
                      Sobre este produto
                    </p>
                    <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">
                      {selectedProduct.description?.trim() ||
                        "Não há uma descrição adicional cadastrada para este produto."}
                    </p>
                  </div>

                  <div className="mt-5 grid gap-2">
                    {productMeta.map(({ label, value, icon: Icon }) => (
                      <div
                        key={label}
                        className="flex items-center gap-3 rounded-xl bg-slate-100 px-3 py-2.5"
                      >
                        <Icon
                          className="h-4 w-4 shrink-0"
                          style={{ color: salonColor }}
                        />
                        <span className="text-xs text-slate-500">{label}</span>
                        <span className="ml-auto text-sm font-medium text-slate-900">
                          {value}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 rounded-xl bg-slate-100 px-3 py-2.5">
                      <CheckCircle2
                        className="h-4 w-4 shrink-0"
                        style={{ color: salonColor }}
                      />
                      <span className="text-xs text-slate-500">
                        Disponibilidade
                      </span>
                      <span className="ml-auto text-sm font-medium text-slate-900">
                        Em estoque
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 bg-white p-4 shadow-[0_-8px_24px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Clock3
                        className="h-4 w-4"
                        style={{ color: salonColor }}
                      />
                      Compre pelo celular
                    </div>
                    <div className="rounded-lg bg-white p-1 ring-1 ring-slate-200">
                      <QRCodeSVG
                        value={storeUrl}
                        size={54}
                        bgColor="#ffffff"
                        fgColor="#0a0a0f"
                        level="M"
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Escaneie o QR Code para abrir a loja, adicionar ao carrinho
                    e enviar o pedido.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
